import { useEffect, useState } from "react";
import { ShieldAlert, FileText, TrendingDown, FileCheck2 } from "lucide-react";
import useCountUp from "@/lib/useCountUp";

/**
 * Mission section rendered on a near-white editorial background to create
 * high-contrast visual break in the otherwise dark page. Uses dark text
 * with deep-red accents for the "luxury newsprint" feel.
 */
export const Mission = () => {
  const { ref: scoreRef, value: score } = useCountUp({ end: 84, duration: 2200 });
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setPulse((p) => !p), 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      className="relative py-20 sm:py-24 md:py-36 overflow-hidden bg-[#f5f5f3]"
      data-testid="mission-section"
    >
      {/* Warm paper-texture ambient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(162,2,11,0.06),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(0,0,0,0.04),transparent_55%)]" />
      {/* Subtle red hairline top + bottom */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#a2020b]/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#a2020b]/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Copy */}
        <div className="btr-reveal">
          <span
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-display font-semibold tracking-[0.18em] uppercase text-[#a2020b] bg-[#a2020b]/10 border border-[#a2020b]/30"
            data-testid="mission-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#a2020b] btr-red-glow" />
            Our Mission
          </span>

          <h2
            className="font-display font-extrabold text-[#111114] text-3xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] sm:leading-[1.02] mt-6 tracking-tight"
            data-testid="mission-headline"
          >
            The greatest losses are the ones that{" "}
            <span className="text-[#a2020b]">could have been prevented</span>.
          </h2>
          <p className="font-body text-zinc-700 text-base sm:text-lg mt-7 leading-relaxed">
            Before The Risk was founded on a single truth: the greatest losses are the ones that could have been prevented.
            We deliver comprehensive pre-loss reporting, risk documentation, and strategic consulting for property owners,
            communities, and attorneys who cannot afford to be unprepared.
          </p>

          <div className="mt-9 grid sm:grid-cols-3 gap-4">
            {[
              { icon: ShieldAlert, label: "Anticipate" },
              { icon: FileText, label: "Document" },
              { icon: TrendingDown, label: "Mitigate" },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.label}
                  className="rounded-md p-4 flex items-center gap-3 bg-white border border-zinc-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-[#a2020b]/50 transition-colors"
                >
                  <Icon className="w-5 h-5 text-[#a2020b]" strokeWidth={1.5} />
                  <span className="font-display text-[#111114] text-sm font-semibold tracking-wide uppercase">
                    {p.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Report visual — light paper theme */}
        <div className="btr-reveal relative aspect-[4/5] max-w-md mx-auto lg:ml-auto w-full">
          {/* Paper stack behind */}
          <div className="absolute inset-0 bg-white rounded-xl rotate-[-3deg] translate-x-3 translate-y-3 border border-zinc-200 shadow-[0_10px_30px_rgba(0,0,0,0.06)] hidden sm:block" />
          <div className="absolute inset-0 bg-white rounded-xl rotate-[2deg] -translate-x-2 translate-y-1 border border-zinc-200 shadow-[0_10px_30px_rgba(0,0,0,0.08)] hidden sm:block" />

          <div
            ref={scoreRef}
            className="absolute inset-0 bg-white rounded-xl overflow-hidden border border-zinc-200 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.18)]"
          >
            {/* Faint grid pattern */}
            <div
              className="absolute inset-0 opacity-60"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
              }}
            />

            <div className="absolute inset-0 p-8 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500">
                    Pre-Loss Report
                  </span>
                  <span className="text-[10px] font-display font-bold tracking-[0.2em] text-[#a2020b]">
                    CONFIDENTIAL
                  </span>
                </div>
                <div className="h-[1px] bg-zinc-300" />
                {[88, 72, 94, 66, 80].map((w, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#a2020b]" />
                    <div className="h-1.5 bg-zinc-200 rounded-full flex-1 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#a2020b] to-[#a2020b]/40 transition-all duration-[1500ms]"
                        style={{
                          width: `${(w * score) / 84}%`,
                          transitionDelay: `${i * 120}ms`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <div className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500">
                  Risk Score
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-extrabold text-[#111114] text-5xl">{score}</span>
                  <span className="text-zinc-500 text-xs font-body">/ 100</span>
                </div>
                <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#a2020b] to-[#c0030e] transition-all duration-[1800ms] ease-out"
                    style={{ width: `${score}%` }}
                  />
                </div>
                <div className="flex items-center justify-between pt-3">
                  <span className="text-[10px] font-body text-zinc-500 tracking-wider uppercase">
                    Status
                  </span>
                  <span
                    className={`text-[10px] font-display font-bold tracking-[0.2em] uppercase px-2 py-1 rounded-sm border transition-colors ${
                      pulse
                        ? "bg-[#a2020b] text-white border-[#a2020b]"
                        : "bg-[#a2020b]/10 text-[#a2020b] border-[#a2020b]/40"
                    }`}
                  >
                    Litigation-Ready
                  </span>
                </div>
              </div>
            </div>

            {/* Red scan line over paper */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                className="absolute left-0 right-0 h-[2px] bg-[#a2020b] shadow-[0_0_14px_#a2020b] opacity-70"
                style={{ animation: "btr-scan 5s ease-in-out infinite" }}
              />
            </div>

            {/* Corner accents */}
            <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[#a2020b]/50" />
            <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[#a2020b]/50" />
          </div>

          {/* Floating flag chip */}
          <div className="absolute -bottom-6 -left-6 bg-white rounded-md p-4 border border-zinc-200 shadow-[0_12px_30px_rgba(0,0,0,0.1)] hidden sm:block btr-tag-pulse">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#a2020b]/10 border border-[#a2020b]/30 flex items-center justify-center">
                <FileCheck2 className="w-4 h-4 text-[#a2020b]" />
              </div>
              <div>
                <div className="font-display text-[#111114] text-xs font-bold">Exposure Flagged</div>
                <div className="font-body text-zinc-500 text-[10px]">3 items require board review</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
